import { useState } from 'react';
import {
  Card,
  Code,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Table,
  TextInput,
  ActionIcon,
  Tooltip,
  Skeleton,
  Alert,
  CopyButton,
} from '@mantine/core';
import {
  IconEye,
  IconEyeOff,
  IconCopy,
  IconSearch,
  IconAlertTriangle,
  IconCheck,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface ClientKey {
  client_num: number;
  hostname: string | null;
  api_key: string;
}
interface KeysResponse {
  data: ClientKey[];
}

const PARTSHUB_BASE_URL = 'https://parts.karmak.app';

function maskKey(k: string): string {
  // ph_c<n>_<hex> → keep prefix + last 4 of hex.
  const parts = k.split('_');
  if (parts.length < 3) {
    return `${k.slice(0, 8)}…`;
  }
  const prefix = `${parts[0]}_${parts[1]}_`;
  const hex = parts.slice(2).join('_');
  return `${prefix}…${hex.slice(-4)}`;
}

interface Example {
  label: string;
  build: (clientNum: number | string, apiKey: string) => string;
}

const EXAMPLES: Example[] = [
  {
    label: 'Paginated',
    build: (n, k) => `curl ${PARTSHUB_BASE_URL}/api/clients/${n}/parts \\\n  -H "x-api-key: ${k}"`,
  },
  {
    label: 'Streaming (NDJSON, one part per line)',
    build: (n, k) =>
      `curl ${PARTSHUB_BASE_URL}/api/clients/${n}/parts/stream \\\n  -H "x-api-key: ${k}"`,
  },
];

export default function ClientKeysPage() {
  const [query, setQuery] = useState('');
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [copiedKey, setCopiedKey] = useState<number | null>(null);
  const [selected, setSelected] = useState<ClientKey | null>(null);

  const q = useQuery({
    queryKey: ['clients', 'keys'],
    queryFn: () => api<KeysResponse>('/api/clients/keys'),
    staleTime: 60_000,
  });

  const all = q.data?.data ?? [];
  const filtered = all.filter((r) => {
    if (!query) {
      return true;
    }
    const qLower = query.toLowerCase();
    return (
      String(r.client_num).includes(qLower) || (r.hostname ?? '').toLowerCase().includes(qLower)
    );
  });

  const toggleReveal = (n: number) =>
    setRevealed((s) => {
      const next = new Set(s);
      if (next.has(n)) {
        next.delete(n);
      } else {
        next.add(n);
      }
      return next;
    });

  const copyKey = async (row: ClientKey) => {
    await navigator.clipboard.writeText(row.api_key);
    setCopiedKey(row.client_num);
    setTimeout(() => setCopiedKey((c) => (c === row.client_num ? null : c)), 1500);
  };

  const clientNum = selected?.client_num ?? '<clientNum>';
  const apiKey = selected?.api_key ?? '<key>';

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={2}>Client API keys</Title>
          <Text c="dimmed" size="sm">
            For Blaze ↔ parts-hub authentication. Each blaze instance uses its own key (
            <Text span ff="monospace" inherit>
              x-api-key
            </Text>{' '}
            header).
          </Text>
        </div>
        <Badge variant="light">{all.length} clients</Badge>
      </Group>

      <Card withBorder padding="md" radius="md">
        <Stack gap="md">
          <Text size="sm" fw={500}>
            Example usage
            {selected ? (
              <Text span size="sm" c="dimmed" inherit>
                {' '}
                — client {selected.client_num}
                {selected.hostname ? ` (${selected.hostname})` : ''}
              </Text>
            ) : (
              <Text span size="sm" c="dimmed" inherit>
                {' '}
                — click a row below to fill in the values
              </Text>
            )}
          </Text>
          {EXAMPLES.map((ex) => {
            const snippet = ex.build(clientNum, apiKey);
            return (
              <Stack key={ex.label} gap={4}>
                <Group justify="space-between" align="center">
                  <Text size="xs" c="dimmed" fw={500}>
                    {ex.label}
                  </Text>
                  <CopyButton value={snippet} timeout={1500}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? 'Copied!' : 'Copy'} withArrow>
                        <ActionIcon
                          variant={copied ? 'filled' : 'subtle'}
                          color={copied ? 'teal' : undefined}
                          size="sm"
                          onClick={copy}
                          aria-label={`Copy ${ex.label} example`}
                        >
                          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
                <Code block>{snippet}</Code>
              </Stack>
            );
          })}
        </Stack>
      </Card>

      {q.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Failed to load keys: {(q.error as Error).message}
        </Alert>
      )}

      <TextInput
        placeholder="Filter by clientNum or hostname"
        leftSection={<IconSearch size={16} />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
      />

      <Card withBorder padding={0} radius="md">
        <Table.ScrollContainer minWidth={700}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 100 }}>Client #</Table.Th>
                <Table.Th>Hostname</Table.Th>
                <Table.Th>API key</Table.Th>
                <Table.Th style={{ width: 100 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {q.isLoading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={14} width={50} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={14} width={180} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={14} width={320} />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={14} width={70} />
                      </Table.Td>
                    </Table.Tr>
                  ))
                : filtered.map((r) => {
                    const isRevealed = revealed.has(r.client_num);
                    const isSelected = selected?.client_num === r.client_num;
                    return (
                      <Table.Tr
                        key={r.client_num}
                        onClick={() => setSelected(r)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected
                            ? 'var(--mantine-primary-color-light)'
                            : undefined,
                        }}
                      >
                        <Table.Td>{r.client_num}</Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {r.hostname ?? '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text ff="monospace" size="xs">
                            {isRevealed ? r.api_key : maskKey(r.api_key)}
                          </Text>
                        </Table.Td>
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          <Group gap={4} justify="flex-end">
                            <Tooltip label={isRevealed ? 'Hide' : 'Reveal'}>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() => toggleReveal(r.client_num)}
                              >
                                {isRevealed ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={copiedKey === r.client_num ? 'Copied!' : 'Copy key'}>
                              <ActionIcon variant="subtle" size="sm" onClick={() => copyKey(r)}>
                                <IconCopy size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
              {!q.isLoading && filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text ta="center" c="dimmed" py="md" size="sm">
                      No matches.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
